import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  MoveHorizontal,
  Info,
} from "lucide-react";

const LongestSubstringVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Example 1",
      s: "abcabcbb",
    },
    {
      id: 2,
      name: "Example 2",
      s: "bbbbb",
    },
    {
      id: 3,
      name: "Complex Case",
      s: "pwwkew",
    },
    {
      id: 4,
      name: "No Repeats",
      s: "abcdefg",
    },
    {
      id: 5,
      name: "Empty String",
      s: "",
    },
    {
      id: 6,
      name: "Single Character",
      s: "a",
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [s, setS] = useState(defaultTestCases[0].s);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function lengthOfLongestSubstring(s):", indent: 0 },
    { line: "charMap = {}", indent: 1 },
    { line: "left = 0, maxLength = 0", indent: 1 },
    { line: "", indent: 0 },
    { line: "for right = 0 to s.length-1:", indent: 1 },
    { line: "currentChar = s[right]", indent: 2 },
    { line: "// If char seen and in current window", indent: 2 },
    { line: "if charMap[currentChar] >= left:", indent: 2 },
    { line: "left = charMap[currentChar] + 1", indent: 3 },
    { line: "", indent: 0 },
    { line: "// Update char's last seen position", indent: 2 },
    { line: "charMap[currentChar] = right", indent: 2 },
    { line: "", indent: 0 },
    { line: "maxLength = max(maxLength, right - left + 1)", indent: 2 },
    { line: "", indent: 0 },
    { line: "return maxLength", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (s) => {
    const allSteps = [];
    if (!s) return [];
    let stepCount = 0;
    let left = 0;
    let maxLength = 0;
    let charMap = {};
    let longestSubstring = "";

    const addStep = (description, highlightLine, details = {}) => {
      const currentSubstring = s.substring(left, (details.right ?? -1) + 1);
      allSteps.push({
        step: stepCount++,
        description,
        highlightLine,
        left,
        maxLength,
        charMap: { ...charMap },
        longestSubstring,
        currentSubstring,
        ...details,
      });
    };

    addStep("Start: Initialize pointers and map.", 2);

    for (let right = 0; right < s.length; right++) {
      const currentChar = s[right];
      addStep(
        `Expand window: 'right' moves to index ${right} ('${currentChar}').`,
        4,
        { right }
      );

      addStep(`Check if '${currentChar}' is in the current window.`, 7, {
        right,
      });
      if (charMap[currentChar] >= left) {
        addStep(`'${currentChar}' found in window. Shrink window.`, 7, {
          right,
          duplicateChar: currentChar,
        });
        left = charMap[currentChar] + 1;
        addStep(`'left' pointer moves to ${left}.`, 8, {
          right,
          duplicateChar: currentChar,
        });
      }

      addStep(
        `Update last seen position of '${currentChar}' to ${right}.`,
        11,
        { right }
      );
      charMap[currentChar] = right;

      const currentLength = right - left + 1;
      addStep(
        `Current window length: ${currentLength}. Max length: ${maxLength}.`,
        13,
        { right }
      );
      if (currentLength > maxLength) {
        maxLength = currentLength;
        longestSubstring = s.substring(left, right + 1);
        addStep(`New max length found: ${maxLength}.`, 13, { right });
      }
    }

    addStep("End of string. Final result found.", 15, {
      right: s.length - 1,
      isFinal: true,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(s);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [s]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setS(testCases[index].s);
  };

  const addCustomTestCase = () => {
    try {
      if (customInput.length === 0) {
        alert("Input string cannot be empty.");
        return;
      }
      const newTestCase = {
        id: Date.now(),
        name: `Custom`,
        s: customInput,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert("Invalid input.");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <MoveHorizontal className="text-blue-500" size={24} /> Longest Substring
        Without Repeating Characters
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given a string, find the length of the longest substring without
                any repeating characters.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">s = "{tc.s}"</div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom string: abac"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4 relative">
              <div className="flex flex-wrap gap-1">
                {(s || "").split("").map((char, idx) => (
                  <div key={idx} className="relative text-center">
                    <div
                      className={`w-10 h-10 flex items-center justify-center border-2 rounded font-mono text-xl font-bold transition-colors duration-300
                      ${
                        currentStepData?.left <= idx &&
                        idx <= currentStepData?.right
                          ? "bg-blue-100 border-blue-300"
                          : "bg-gray-100 border-gray-300"
                      }
                      ${
                        idx === currentStepData?.right
                          ? "border-blue-500 scale-110"
                          : ""
                      }
                      ${
                        s[idx] === currentStepData?.duplicateChar &&
                        currentStepData?.highlightLine === 7
                          ? "bg-red-200 border-red-400"
                          : ""
                      }
                    `}
                    >
                      {char}
                    </div>
                    <span className="text-xs text-gray-400 mt-1">{idx}</span>
                  </div>
                ))}
              </div>
              <div
                className={`absolute -bottom-6 h-1 bg-blue-500 transition-all duration-300`}
                style={{
                  left: `${(currentStepData?.left || 0) * 44}px`,
                  width: `${
                    ((currentStepData?.right ?? -1) -
                      (currentStepData?.left ?? 0) +
                      1) *
                      44 -
                    4
                  }px`,
                }}
              >
                <span className="absolute -top-6 text-xs font-bold text-blue-600">
                  L
                </span>
                <span className="absolute -top-6 right-0 text-xs font-bold text-blue-600">
                  R
                </span>
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-4 text-center">
              <div>
                <h4 className="text-sm font-semibold text-gray-700">
                  Current Substring
                </h4>
                <p className="p-2 bg-blue-50 text-blue-800 font-mono rounded mt-1 truncate">
                  {currentStepData?.currentSubstring || " "}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700">
                  Max Length
                </h4>
                <p className="p-2 bg-green-50 text-green-800 font-bold text-lg rounded mt-1">
                  {currentStepData?.maxLength || 0}
                </p>
              </div>
              <div className="col-span-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  Character Map (last seen index)
                </h4>
                <div className="p-2 bg-gray-50 text-gray-800 rounded mt-1 flex flex-wrap gap-2 justify-center">
                  {currentStepData &&
                    Object.keys(currentStepData.charMap).map((key) => (
                      <span
                        key={key}
                        className="font-mono text-xs p-1 bg-gray-200 rounded"
                      >
                        {key}: {currentStepData.charMap[key]}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Sliding Window Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The problem is solved efficiently using a "sliding window"
              approach with two pointers, `left` and `right`. The `right`
              pointer expands the window, and a hash map tracks the last seen
              index of each character. If a repeating character is found within
              the current window, the `left` pointer jumps forward to shrink the
              window, ensuring it remains valid.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(min(N, M))
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N = length of string, M = size of character set.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The sliding window only moves forward, ensuring each character
                  is visited at most twice (by the right and left pointers).
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Using a hash map to store character indices allows for an O(1)
                  lookup to find duplicates.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The `left` pointer only moves when a duplicate is found
                  *inside* the current window, which is a crucial optimization.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-blue-100 border border-blue-300">
                  abc
                </div>
                <span>Current Window</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded border-2 border-blue-500"></div>
                <span>Right Pointer (Expanding)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-red-200 border-red-400"></div>
                <span>Duplicate Found</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LongestSubstringVisualizer;
